import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { SEO_PAGE_HEADERS } from "~/lib/cache.server"
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { serviceCategories, professionalProfiles, professionalCategories, geoFrance } from '@afficheservices/db/schema'
import { eq, and, sql, desc, isNull } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { SERVICE_LABELS, SERVICE_PRICES } from '~/lib/programmatic-seo'
import { TASKS_BY_SERVICE, DEAD_TASK_SLUGS } from '~/lib/tarifs-tasks-data'
import { InlineQuiz } from '~/components/inline-quiz'
import { getSeoContent } from '~/lib/seo-content.server'
import { ContentMeta, SourceCitations, InternaunteQuestions } from '~/components/content-trust'
import { FAQSchema, BreadcrumbSchema } from '~/components/structured-data'
import { Euro, Star, ArrowRight, BadgeCheck, Calculator } from 'lucide-react'
import { formatCityName } from '~/lib/city-format'
import { assertCityNotDomTom } from '~/lib/dom-tom.server'

function slugToArtisan(artisanSlug: string): { serviceSlug: string; artisanName: string } | null {
  for (const [slug, svc] of Object.entries(SERVICE_LABELS)) {
    const normalized = svc.artisan.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    if (normalized === artisanSlug) return { serviceSlug: slug, artisanName: svc.artisan }
  }
  return null
}

export async function loader({ params }: LoaderFunctionArgs) {
  const { artisan: artisanSlug, city: citySlug } = params
  const parsed = slugToArtisan(artisanSlug ?? '')
  if (!parsed) throw new Response('Page non trouvée', { status: 404 })

  await assertCityNotDomTom(citySlug)

  const { serviceSlug, artisanName } = parsed
  const cityName = formatCityName(citySlug)
  const svcInfo = SERVICE_LABELS[serviceSlug]
  const price = SERVICE_PRICES[serviceSlug]

  // Find pros
  const [cat] = await db
    .select({ id: serviceCategories.id })
    .from(serviceCategories)
    .where(eq(serviceCategories.slug, serviceSlug))
    .limit(1)

  let pros: any[] = []
  if (cat) {
    pros = await db
      .select({
        companyName: professionalProfiles.companyName,
        slug: professionalProfiles.slug,
        city: professionalProfiles.city,
        ratingAvg: professionalProfiles.ratingAvg,
        reviewCount: professionalProfiles.reviewCount,
        identityVerified: professionalProfiles.identityVerified,
        isDemo: professionalProfiles.isDemo,
      })
      .from(professionalProfiles)
      .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
      .where(and(eq(professionalCategories.categoryId, cat.id), eq(professionalProfiles.identityVerified, true)))
      .orderBy(desc(professionalProfiles.ratingAvg))
      .limit(6)
  }

  // Related cities
  const relatedCities = await db
    .select({ name: geoFrance.communeName })
    .from(geoFrance)
    .where(sql`${geoFrance.communeName} != ${cityName}`)
    .orderBy(sql`RANDOM()`)
    .limit(8)

  // Related services
  const relatedServices = await db
    .select({ name: serviceCategories.nameFr, slug: serviceCategories.slug })
    .from(serviceCategories)
    .where(and(isNull(serviceCategories.parentId), sql`${serviceCategories.slug} != ${serviceSlug}`))
    .orderBy(sql`RANDOM()`)
    .limit(5)

  const faqs = [
    { question: `Quel est le tarif moyen d'un ${artisanName} à ${cityName} ?`, answer: price ? `Les tarifs d'un ${artisanName} à ${cityName} varient de ${price.min} € à ${price.max} € par ${price.unit}. Le prix dépend du type d'intervention, de la complexité et de l'accès au chantier.` : `Les tarifs varient selon le type de travaux. Demandez des devis gratuits pour un prix exact.` },
    { question: `Comment trouver un bon ${artisanName} à ${cityName} ?`, answer: `Comparez les devis de 3 ${artisanName}s vérifiés sur AfficheServices. Vérifiez les avis, l'assurance RC Pro et l'expérience. Privilégiez les artisans locaux.` },
    { question: `Le tarif inclut-il les fournitures ?`, answer: `Cela dépend du devis. Demandez toujours une ventilation main d'œuvre / fournitures. Acheter le matériel vous-même peut faire économiser 20-30%.` },
  ]

  return { serviceSlug, artisanName, cityName, svcInfo, price, pros, relatedCities: relatedCities.map(c => c.name), relatedServices, faqs, slug: `${artisanSlug}/${citySlug}`, aiContent: await getSeoContent(serviceSlug, cityName) }
}

export function headers() { return SEO_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const year = new Date().getFullYear()
  const tags: ReturnType<MetaFunction> = [
    { title: `Tarif ${data?.artisanName} à ${data?.cityName} ${year} — Prix et devis | AfficheServices` },
    { name: 'description', content: `Tarifs ${data?.artisanName} à ${data?.cityName} en ${year}. ${data?.price ? `De ${data.price.min} € à ${data.price.max} €.` : ''} Comparez les prix et obtenez 3 devis gratuits.` },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/tarifs/${data?.slug}` },
  ]
  // noindex until rebuild with real per-city DB data (audit 2026-04-17: 4.5% uniqueness)
  tags.push({ name: 'robots', content: 'noindex, follow' })
  return tags
}

function slugify(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
}

export default function TarifsArtisanCity() {
  const { serviceSlug, artisanName, cityName, svcInfo, price, pros, relatedCities, relatedServices, faqs, slug, aiContent } = useLoaderData<typeof loader>()
  // This 2-seg hub is the parent of the 4-seg /tarifs/{svc}/{city}/{task} money
  // pages. It's noindex,follow — but "follow" means Googlebot crawls these links,
  // giving the (indexable) task pages a discovery parent they previously lacked
  // (GSC audit 2026-07: the 4-seg pages were sitemap-only orphans).
  const citySlug = slug.split('/')[1] ?? ''
  const prestations = (TASKS_BY_SERVICE[serviceSlug] ?? []).filter((t) => !DEAD_TASK_SLUGS.has(t.slug))

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <FAQSchema items={faqs} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Tarifs', url: 'https://www.afficheservices.fr/guide-prix' },
        { name: `Tarif ${artisanName} ${cityName}`, url: `https://www.afficheservices.fr/tarifs/${slug}` },
      ]} />

      <div className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-8 text-white">
          <img
            src={`/api/og?title=${encodeURIComponent(`Tarif ${artisanName} à ${cityName}`)}&subtitle=${encodeURIComponent('Grille de prix · Devis gratuit')}`}
            alt={`Tarif ${artisanName} à ${cityName}`}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-15 mix-blend-overlay"
            loading="eager"
            width={1200}
            height={630}
          />
          <div className="relative">
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Euro className="h-4 w-4" /> Guide des tarifs {new Date().getFullYear()}
            </div>
            <h1 className="mt-2 text-2xl font-bold text-white sm:text-3xl">
              Tarif {artisanName} à {cityName}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Combien coûte un {artisanName} à {cityName} en {new Date().getFullYear()} ? Voici les tarifs moyens constatés et nos conseils pour payer le juste prix.
            </p>
            {price && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 backdrop-blur-sm">
                <Calculator className="h-4 w-4" />
                <span className="text-sm font-semibold">{price.min} — {price.max} € / {price.unit}</span>
              </div>
            )}
          </div>
        </div>

        {/* Price grid */}
        {price && (
          <div className="mt-6 grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-emerald-50 p-4 text-center">
              <p className="text-[10px] font-semibold uppercase text-emerald-600">Minimum</p>
              <p className="mt-1 text-2xl font-bold text-emerald-700">{price.min} €</p>
            </div>
            <div className="rounded-xl bg-primary-50 p-4 text-center">
              <p className="text-[10px] font-semibold uppercase text-primary">Moyen</p>
              <p className="mt-1 text-2xl font-bold text-primary">{Math.round((price.min + price.max) / 2)} €</p>
            </div>
            <div className="rounded-xl bg-amber-50 p-4 text-center">
              <p className="text-[10px] font-semibold uppercase text-amber-600">Maximum</p>
              <p className="mt-1 text-2xl font-bold text-amber-700">{price.max} €</p>
            </div>
          </div>
        )}

        {/* SEO content */}
        <article className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Tarifs {artisanName} à {cityName} en détail</h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
            <p>
              Les tarifs d'un {artisanName} à {cityName} dépendent de plusieurs facteurs : le type d'intervention,
              la complexité des travaux, l'urgence et l'accessibilité du chantier. En 2026, les prix sont stables
              avec une légère hausse due à l'inflation des matériaux.
            </p>
            <p>
              Pour obtenir le meilleur prix, nous recommandons de comparer au moins 3 devis de {artisanName}s
              vérifiés à {cityName}. Sur AfficheServices, les professionnels sont contrôlés (SIRET, assurance)
              et vous recevez des devis sous 48h.
            </p>
          </div>
        </article>

        {/* Pros */}
        {pros.filter(p => !p.isDemo).length > 0 && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
            <h2 className="text-lg font-bold text-slate-900">{svcInfo?.name ?? 'Artisans'} à {cityName}</h2>
            <div className="mt-4 space-y-3">
              {pros.filter(p => !p.isDemo).map((pro) => (
                <Link key={pro.slug} to={`/pro/${pro.slug}`}
                  className="flex items-center gap-3 rounded-xl bg-slate-50 p-3 transition-colors hover:bg-primary-50">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary">
                    {pro.companyName[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">{pro.companyName}</span>
                      {pro.identityVerified && <BadgeCheck className="h-3.5 w-3.5 text-primary" />}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {Number(pro.ratingAvg) > 0 && <><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {pro.ratingAvg}</>}
                      <span>· {pro.city}</span>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-300" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* AI expert content */}
        {aiContent && (
          <>
            <article className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
              <ContentMeta cityName={cityName} serviceName={svcInfo?.name ?? 'Travaux'} />
              <h2 className="mt-3 text-lg font-bold text-slate-900">Tarifs {artisanName} à {cityName} — guide expert</h2>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
                {aiContent.expertIntroParas.map((p, i) => <p key={i}>{p}</p>)}
              </div>
              <SourceCitations serviceSlug={serviceSlug} />
            </article>
            {aiContent.localContextParas.length > 0 && (
              <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50 p-6">
                <h3 className="text-base font-bold text-blue-900">Spécificités locales — {cityName}</h3>
                <div className="mt-3 space-y-2 text-sm leading-relaxed text-blue-800">
                  {aiContent.localContextParas.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            )}
            {aiContent.priceAnalysis && (
              <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
                <h3 className="text-base font-bold text-emerald-900">Analyse des tarifs — {cityName}</h3>
                <p className="mt-3 text-sm leading-relaxed text-emerald-800">{aiContent.priceAnalysis}</p>
              </div>
            )}
            <InternaunteQuestions serviceSlug={serviceSlug} cityName={cityName} artisan={artisanName} />
          </>
        )}

        {/* FAQ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Questions fréquentes</h2>
          <div className="mt-4 space-y-4">
            {(aiContent?.faqItems ?? faqs.map(f => ({ q: f.question, a: f.answer }))).map((faq, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-slate-900">{faq.q}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Prestations — links to the 4-seg /tarifs/{svc}/{city}/{task} money
            pages so Googlebot discovers them from their parent hub. */}
        {prestations.length > 0 && (
          <div className="mt-8 rounded-xl border border-border bg-white p-5 shadow-xs">
            <h2 className="text-base font-bold text-slate-900">Tarifs détaillés {artisanName} à {cityName}</h2>
            <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
              {prestations.map((t) => (
                <Link
                  key={t.slug}
                  to={`/tarifs/${serviceSlug}/${citySlug}/${t.slug}`}
                  className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-700 hover:border-primary hover:text-primary"
                >
                  <span>{t.label}</span>
                  <span className="ml-2 shrink-0 font-semibold text-slate-500">{t.priceRange}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Internal links */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-white p-4 shadow-xs">
            <h3 className="text-xs font-semibold text-slate-500">Tarifs {artisanName} — autres villes</h3>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {relatedCities.map((city) => (
                <Link key={city} to={`/tarifs/${slugify(artisanName)}-${slugify(city)}`}
                  className="rounded-full border border-border px-2.5 py-1 text-[11px] text-slate-600 hover:border-primary hover:text-primary">
                  Tarif {artisanName} {city}
                </Link>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-white p-4 shadow-xs">
            <h3 className="text-xs font-semibold text-slate-500">Autres tarifs à {cityName}</h3>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {relatedServices.map((rs) => {
                const rsLabel = SERVICE_LABELS[rs.slug]
                return (
                  <Link key={rs.slug} to={`/tarifs/${slugify(rsLabel?.artisan ?? rs.slug)}/${slugify(cityName)}`}
                    className="rounded-full border border-border px-2.5 py-1 text-[11px] text-slate-600 hover:border-primary hover:text-primary">
                    Tarif {rsLabel?.artisan ?? rs.name} {cityName}
                  </Link>
                )
              })}
            </div>
          </div>
        </div>

        {/* Quiz */}
        <div className="mt-8">
          <InlineQuiz serviceName={svcInfo?.name ?? 'Service'} cityName={cityName} categorySlug={serviceSlug} />
        </div>

        {/* CTA */}
        <div className="mt-8 rounded-2xl bg-slate-900 p-8 text-center">
          <h2 className="text-xl font-bold text-white">Obtenez un tarif exact à {cityName}</h2>
          <p className="mt-2 text-sm text-slate-400">3 devis gratuits de {artisanName}s vérifiés sous 48h</p>
          <Link to="/demande-devis/1" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press">
            Devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <Footer contextTitle={`Tarifs ${artisanName} — Autres villes`} contextLinks={
        relatedCities.slice(0, 6).map((city) => ({
          href: `/tarifs/${slugify(artisanName)}/${slugify(city)}`,
          label: `Tarif ${artisanName} ${city}`,
        }))
      } />
    </div>
  )
}
